import {
  Anchor,
  Badge,
  Button,
  Divider,
  Flex,
  Loader,
  Popover,
  Text,
  Tooltip,
} from "@mantine/core";
import dayjs from "dayjs";
import { Fragment, useCallback, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import ConfirmationButton from "src/components/shared/ConfirmationButton/ConfirmationButton";
import { useSelector } from "src/redux/hooks";
import type { IPendingRequest } from "src/redux/slices/pendingRequestsSlice";
import styles from "./PendingRequestsInfo.module.scss";

const getStatusLabel = (status: string) => {
  switch (status) {
    case "pending":
      return "Pending";
    case "uploading":
      return "Uploading";
    case "done":
      return "Done";
    case "failed":
      return "Failed";
    case "pending-new-entry":
      return "Waiting";
    default:
      return status;
  }
};

const getStatusColor = (status: string) => {
  switch (status) {
    case "done":
      return "green";
    case "failed":
      return "red";
    case "uploading":
      return "blue";
    case "pending-new-entry":
      return "gray";
    default:
      return "yellow";
  }
};

const UploadStatusBadge = ({ status }: { status: string }) => {
  if (status === "uploading") {
    return (
      <Flex align="center" gap={6}>
        <Loader size="xs" />
        <Text size="xs">Uploading</Text>
      </Flex>
    );
  }

  return (
    <Badge size="xs" color={getStatusColor(status)} variant="light">
      {getStatusLabel(status)}
    </Badge>
  );
};

const UploadRow = ({
  req,
  onNavigate,
  onRetry,
}: {
  req: IPendingRequest;
  onNavigate: (route?: string | null) => void;
  onRetry: (reqId: string) => void;
}) => {
  const label = `${req.action}${req.lbname ? ` (${req.lbname})` : ""}`;
  const createdLabel = dayjs(req.created).format("D MMM YYYY HH:mm");
  const uploadedLabel =
    req.uploaded && req.status === "done"
      ? dayjs(req.uploaded).format("D MMM YYYY HH:mm")
      : null;

  return (
    <Fragment key={req.req}>
      <Flex align="center" justify="space-between" gap="sm" py={4}>
        <Flex direction="column" gap={2} style={{ flex: 1, minWidth: 0 }}>
          {req.route ? (
            <Anchor
              component="button"
              type="button"
              size="xs"
              ta="left"
              onClick={() => onNavigate(req.route)}>
              {label}
            </Anchor>
          ) : (
            <Text size="xs">{label}</Text>
          )}
          <Text size="xs" c="dimmed">
            {createdLabel}
            {uploadedLabel ? ` · Uploaded ${uploadedLabel}` : ""}
          </Text>
          {req.status === "failed" && req.error ? (
            <Tooltip label={req.error} multiline maw={320}>
              <Text size="xs" c="red" lineClamp={2}>
                {req.error}
              </Text>
            </Tooltip>
          ) : null}
        </Flex>
        <Flex align="center" gap={8} style={{ flexShrink: 0 }}>
          <UploadStatusBadge status={req.status} />
          {req.status === "failed" ? (
            <Button
              size="compact-xs"
              variant="light"
              onClick={() => onRetry(req.req)}>
              Retry
            </Button>
          ) : null}
        </Flex>
      </Flex>
      <Divider />
    </Fragment>
  );
};

const PendingRequestsInfo = () => {
  const [opened, setOpened] = useState(false);
  const navigate = useNavigate();

  const pendingRequests = useSelector(
    state => state.pendingRequestsSlice.pendingRequests,
  );

  const uploadSummary = useMemo(() => {
    const pendingCount = pendingRequests.filter(req =>
      ["pending", "uploading", "failed"].includes(req.status),
    ).length;
    const doneCount = pendingRequests.filter(
      req => req.status === "done",
    ).length;

    if (pendingCount > 0) {
      return `Requests: ${pendingRequests.length} (Pending: ${pendingCount})`;
    }

    return `Requests: ${pendingRequests.length}${doneCount > 0 ? ` · Done: ${doneCount}` : ""}`;
  }, [pendingRequests]);

  const hasCompletedRequests = useMemo(
    () => pendingRequests.some(req => req.status === "done"),
    [pendingRequests],
  );

  const toggle = useCallback(() => {
    setOpened(prev => !prev);
  }, []);

  const handleNavigate = useCallback(
    (route?: string | null) => {
      if (!route) {
        return;
      }
      setOpened(false);
      navigate(route);
    },
    [navigate],
  );

  const handleRetry = useCallback(async (reqId: string) => {
    if (!window.api?.retryUpload) {
      return;
    }

    await window.api.retryUpload(reqId);
  }, []);

  const handleClearCompleted = useCallback(async () => {
    if (!window.api?.clearCompletedRequests) {
      return;
    }

    await window.api.clearCompletedRequests();
  }, []);

  if (pendingRequests.length === 0) {
    return null;
  }

  return (
    <Popover opened={opened} onChange={setOpened} keepMounted>
      <Popover.Target>
        <Button ml="auto" variant="white" onClick={toggle}>
          {uploadSummary}
        </Button>
      </Popover.Target>
      <Popover.Dropdown
        maw={400}
        mah={400}
        className={styles.pendingDropdown}
        style={{ overflowY: "auto", width: "100%" }}>
        {hasCompletedRequests ? (
          <>
            <Flex justify="flex-end" pb="xs">
              <ConfirmationButton
                modalText="Are you sure you want to clear out completed requests ?"
                onConfirm={handleClearCompleted}
                buttonProps={{ size: "compact-xs", variant: "subtle" }}>
                Clear completed
              </ConfirmationButton>
            </Flex>
            <Divider />
          </>
        ) : null}
        {pendingRequests.map(req => (
          <UploadRow
            key={req.req}
            req={req}
            onNavigate={handleNavigate}
            onRetry={handleRetry}
          />
        ))}
      </Popover.Dropdown>
    </Popover>
  );
};

export default PendingRequestsInfo;
